import { useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * Session timeout hook - automatically logs out users after inactivity
 * @param {number} timeoutMinutes - Minutes of inactivity before logout (default: 30)
 * @param {number} warningMinutes - Minutes before timeout to show warning (default: 5)
 */
function useSessionTimeout(timeoutMinutes = 30, warningMinutes = 5) {
  const navigate = useNavigate();
  const location = useLocation();
  const timeoutRef = useRef(null);
  const warningRef = useRef(null);
  const warningShownRef = useRef(false);

  // Pages that don't require session timeout (exact matches only for root, prefix for others)
  const publicPaths = ['/forgot-password', '/reset-password', '/password-reset-success'];
  const isPublicPage = location.pathname === '/' || publicPaths.some(path => location.pathname.startsWith(path));

  // Check if user is authenticated
  const isAuthenticated = useCallback(() => {
    const token = localStorage.getItem('authToken');
    const role = localStorage.getItem('role') || localStorage.getItem('userRole');
    return !!(token && role);
  }, []);

  // Logout function
  const logout = useCallback(() => {
    console.log('[Session] Logging out due to inactivity');
    localStorage.removeItem('authToken');
    localStorage.removeItem('userRole');
    localStorage.removeItem('role');
    localStorage.removeItem('userId');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userName');
    localStorage.removeItem('lastActivity');
    navigate('/?error=Session expired due to inactivity');
  }, [navigate]);

  // Show warning before logout
  const showWarning = useCallback(() => {
    if (warningShownRef.current) return;
    warningShownRef.current = true;

    // Create warning modal
    const overlay = document.createElement('div');
    overlay.id = 'session-warning-overlay';
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10000;
    `;

    const modal = document.createElement('div');
    modal.style.cssText = `
      background: white;
      padding: 32px;
      border-radius: 12px;
      max-width: 400px;
      text-align: center;
      box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
    `;

    const remainingMinutes = warningMinutes;
    modal.innerHTML = `
      <h2 style="margin: 0 0 16px; color: #333; font-size: 20px;">Session Expiring Soon</h2>
      <p style="margin: 0 0 24px; color: #666; line-height: 1.5;">
        Your session will expire in <strong id="countdown">${remainingMinutes}</strong> minute${remainingMinutes > 1 ? 's' : ''} due to inactivity.
      </p>
      <button id="stay-logged-in" style="
        background: linear-gradient(135deg, #e91e63 0%, #ff9800 100%);
        color: white;
        border: none;
        padding: 12px 24px;
        font-size: 16px;
        font-weight: 600;
        border-radius: 8px;
        cursor: pointer;
        margin-right: 12px;
      ">Stay Logged In</button>
      <button id="logout-now" style="
        background: white;
        color: #666;
        border: 2px solid #ddd;
        padding: 12px 24px;
        font-size: 16px;
        font-weight: 600;
        border-radius: 8px;
        cursor: pointer;
      ">Logout Now</button>
    `;

    overlay.appendChild(modal);
    document.body.appendChild(overlay);

    // Countdown timer
    let secondsRemaining = remainingMinutes * 60;
    const countdownElement = document.getElementById('countdown');
    const countdownInterval = setInterval(() => {
      secondsRemaining--;
      const mins = Math.floor(secondsRemaining / 60);
      const secs = secondsRemaining % 60;
      if (countdownElement) {
        countdownElement.textContent = mins > 0 ? `${mins}:${secs.toString().padStart(2, '0')}` : `${secs}s`;
      }
      if (secondsRemaining <= 0) {
        clearInterval(countdownInterval);
      }
    }, 1000);

    // Stay logged in button
    document.getElementById('stay-logged-in')?.addEventListener('click', () => {
      clearInterval(countdownInterval);
      overlay.remove();
      warningShownRef.current = false;
      resetTimer();
    });

    // Logout now button
    document.getElementById('logout-now')?.addEventListener('click', () => {
      clearInterval(countdownInterval);
      overlay.remove();
      logout();
    });
  }, [warningMinutes, logout]);

  // Reset activity timer
  const resetTimer = useCallback(() => {
    if (!isAuthenticated() || isPublicPage) return;

    // Clear existing timers
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningRef.current) clearTimeout(warningRef.current);

    // Remove warning if shown
    const existingOverlay = document.getElementById('session-warning-overlay');
    if (existingOverlay) {
      existingOverlay.remove();
      warningShownRef.current = false;
    }

    // Update last activity
    localStorage.setItem('lastActivity', Date.now().toString());

    // Set warning timer
    const warningTime = (timeoutMinutes - warningMinutes) * 60 * 1000;
    warningRef.current = setTimeout(() => {
      showWarning();
    }, warningTime);

    // Set logout timer
    const logoutTime = timeoutMinutes * 60 * 1000;
    timeoutRef.current = setTimeout(() => {
      logout();
    }, logoutTime);

  }, [isAuthenticated, isPublicPage, timeoutMinutes, warningMinutes, showWarning, logout]);

  // Check for expired session on mount (e.g., if browser was left open)
  useEffect(() => {
    if (!isAuthenticated() || isPublicPage) return;

    const lastActivity = localStorage.getItem('lastActivity');
    if (lastActivity) {
      const elapsed = Date.now() - parseInt(lastActivity, 10);
      const timeoutMs = timeoutMinutes * 60 * 1000;
      if (elapsed > timeoutMs) {
        logout();
        return;
      }
    } else {
      // No lastActivity means user just logged in - initialize it now
      localStorage.setItem('lastActivity', Date.now().toString());
    }

    resetTimer();
  }, [isAuthenticated, isPublicPage, timeoutMinutes, resetTimer, logout]);

  // Listen for user activity
  useEffect(() => {
    if (!isAuthenticated() || isPublicPage) return;

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'];

    // Throttle activity updates to every 30 seconds to avoid excessive updates
    let lastUpdate = Date.now();
    const throttledReset = () => {
      const now = Date.now();
      if (now - lastUpdate > 30000) { // Only update every 30 seconds
        lastUpdate = now;
        resetTimer();
      }
    };

    events.forEach(event => {
      window.addEventListener(event, throttledReset, { passive: true });
    });

    // Initial timer setup
    resetTimer();

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, throttledReset);
      });
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningRef.current) clearTimeout(warningRef.current);
    };
  }, [isAuthenticated, isPublicPage, resetTimer]);

  return { resetTimer };
}

export default useSessionTimeout;
